'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { NotificationsBell } from '@/new_components/notifications/NotificationsBell';
import { CreditsPill } from '@/new_components/shared/CreditsPill';
import LandingSearch from '@/new_components/shared/LandingSearch';
import { UserDropdownMenu } from '@/app/components/partials/topbar/user-dropdown-menu';
import { AppDrawer } from '@/new_components/app-shell/AppDrawer';
import { Menu, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Container } from '@/components/common/container';
import { LocaleLink } from '@/components/locale-link';
import { useHomeSearchTarget } from '@/new_components/home-live/home-search-merge';

const FILTER_PAGES = ['/search', '/insolvenzticker', '/vergabe'];

const MegaMenuMobile = dynamic(
  () =>
    import('./mega-menu-mobile').then((m) => ({ default: m.MegaMenuMobile })),
  { ssr: false },
);

/**
 * Two-row mobile header. Top row holds the hamburger, full logo+wordmark, and
 * the user dropdown. The search bar sits on its own row below so it has the
 * full content width to itself. On /search the row also shows a filter
 * button with an active-filter badge.
 *
 * Owns the small bits of state the rows need (sheet open, search expansion,
 * filter count) so `header.tsx` can stay declarative.
 */
export function HeaderMobile({
  // Display utility for the root. Defaults to the CSS `xl` breakpoint so the
  // mobile header shows below xl during SSR / before header.tsx's JS
  // fit-measurement resolves. Once measured, header.tsx passes `flex` (force
  // mobile) or `hidden` (desktop fits) to override the breakpoint.
  display = 'flex xl:hidden',
  variant = 'guest',
}: {
  display?: string;
  variant?: 'guest' | 'app';
} = {}) {
  const pathname = usePathname();
  const { t } = useTranslation();
  const { internalPathname } = useLocale();
  const isSearchPage = internalPathname === '/search';
  // The homepage's hero search sits right below; below lg it has no second row.
  const isHome = internalPathname === '/';
  const isFilterPage = FILTER_PAGES.includes(internalPathname);
  const homeSearch = useHomeSearchTarget();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [, setIsMobileSearchExpanded] = useState(false);
  const [filterCount, setFilterCount] = useState({ pathname: internalPathname, count: 0 });
  const activeFilterCount = filterCount.pathname === internalPathname ? filterCount.count : 0;
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [previousPathname, setPreviousPathname] = useState(pathname);
  if (previousPathname !== pathname) {
    setPreviousPathname(pathname);
    setIsSheetOpen(false);
    setIsMobileSearchExpanded(false);
  }

  // Publish the mobile header's measured height as `--mobile-header-height` so
  // overlays (e.g. the /search filter panel) can anchor to it without magic
  // numbers. Same pattern as Footer publishing `--footer-height`.
  useEffect(() => {
    const updateHeight = () => {
      const h = wrapperRef.current?.offsetHeight;
      if (!h) return;
      document.body.style.setProperty('--mobile-header-height', `${h}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    if (!isFilterPage) return;
    const onCount = (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      setFilterCount({ pathname: internalPathname, count: typeof detail === 'number' ? detail : 0 });
    };
    window.addEventListener('firmendata:filter-count', onCount);
    return () => window.removeEventListener('firmendata:filter-count', onCount);
  }, [isFilterPage, internalPathname]);

  const collapseMobileSearch = () => {
    const input = mobileSearchRef.current?.querySelector('input');
    input?.blur();
    setIsMobileSearchExpanded(false);
  };

  return (
    <div ref={wrapperRef} className={cn(display, 'flex-col w-full')}>
      {/* Row 1: hamburger + logo (left) | credits, bell, user (right).
          Left-packed rather than `justify-between`: with three items the
          wordmark was centred, which parked dead space between it and the
          hamburger while the right-hand controls fought for room. `mr-auto` on
          the logo pins the controls to the right edge and hands every spare
          pixel to the middle instead. */}
      <Container className={cn('group/row flex items-center gap-2 px-4 pt-3', isHome && 'max-lg:pb-3')}>
        {variant === 'app' ? (
          <>
            <Button
              variant="dim"
              mode="icon"
              aria-label={t('navigation.openMenu')}
              className="min-h-[44px] min-w-[44px] shrink-0 focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setIsSheetOpen(true)}
            >
              <Menu />
            </Button>
            <AppDrawer open={isSheetOpen} onOpenChange={setIsSheetOpen} />
          </>
        ) : <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="dim"
              mode="icon"
              aria-label="Open menu"
              className="min-h-[44px] min-w-[44px] shrink-0"
            >
              <Menu />
            </Button>
          </SheetTrigger>
          <SheetContent
            className="p-0 gap-0 w-[275px]"
            side="left"
            close={false}
          >
            <SheetHeader className="p-0 space-y-0 shrink-0" />
            <SheetBody className="p-0 flex flex-col grow min-h-0 overflow-y-auto overscroll-contain">
              <MegaMenuMobile />
            </SheetBody>
          </SheetContent>
        </Sheet>}

        <LocaleLink
          href={variant === 'app' ? '/overview' : '/'}
          className={cn(
            'flex items-center gap-2 min-h-[44px] min-w-0 mr-auto group',
            variant === 'app' && 'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <Image
            src="/images/logo.svg"
            width={44}
            height={44}
            className="w-10 h-10 shrink-0 object-contain"
            alt="FirmenData Logo"
            priority
            unoptimized
          />
          {/* Wordmark yields to the controls instead of painting over them.
              A signed-in user carries a credits pill and a bell, and on a
              360-414px phone the row simply cannot hold those, the hamburger,
              the mark AND ten characters at 20px — it ran ~80px over and the
              overflow landed on top of the pill's border. `:has()` keys the
              wordmark off the pill's presence, so guests (no pill, no bell)
              keep the full lockup and only the crowded signed-in row drops to
              the mark. Doing it in CSS rather than off `useSession()` keeps the
              server and client markup identical. */}
          <span
            className={cn(
              'inline-flex items-baseline whitespace-nowrap text-[20px] tracking-[-0.025em] leading-none',
              variant === 'app' ? 'hidden' : 'max-sm:group-has-[[data-credits-pill]]/row:hidden',
            )}
            style={{ color: 'var(--fd-ink)' }}
          >
            <span className="font-bold">Firmen</span>
            <span className="font-normal">data</span>
          </span>
        </LocaleLink>

        <div className={cn(
          'flex shrink-0 items-center gap-1',
          variant === 'app' && '[&_button]:min-h-10 [&_button]:min-w-10 [&_button]:focus-visible:outline-none [&_button]:focus-visible:ring-2 [&_button]:focus-visible:ring-ring [&_a]:min-h-10 [&_a]:focus-visible:outline-none [&_a]:focus-visible:ring-2 [&_a]:focus-visible:ring-ring',
        )}>
          {/* Compact credit balance + bell — both null for guests. */}
          <CreditsPill compact />
          <NotificationsBell />
          {variant === 'guest' && <UserDropdownMenu />}
        </div>
      </Container>

      {/* Row 2: search bar with an embedded filter button on /search. On
          /search the row gets extra breathing room so the header reads
          visibly thicker. */}
      <Container
        className={cn(
          'flex items-center gap-2 px-4 lg:px-4',
          isSearchPage ? 'pt-3 pb-5' : 'pt-2 pb-3',
          isHome && 'max-lg:hidden',
        )}
      >
        <div
          ref={mobileSearchRef}
          onFocus={() => setIsMobileSearchExpanded(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setIsMobileSearchExpanded(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              collapseMobileSearch();
            }
          }}
          onSubmit={collapseMobileSearch}
          className="flex items-center justify-center grow min-w-0"
        >
          {/* Homepage: revealed as the hero search scrolls up into the header. */}
          <div className="flex w-full min-w-0 justify-center" {...homeSearch}>
            <LandingSearch
              compact
              className="max-w-none"
              largeOnMobile={isSearchPage}
              trailingButton={
                isFilterPage ? (
                  <button
                    type="button"
                    aria-label={t('search.openFilters')}
                    onMouseDown={(e) => {
                      // Keep the input focus state stable so blurring doesn't
                      // race with the click handler.
                      e.preventDefault();
                    }}
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('firmendata:open-filters'),
                      );
                    }}
                    className="relative inline-flex size-11 lg:size-8 shrink-0 items-center justify-center rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors"
                    style={{
                      color: 'var(--fd-ink-2)',
                    }}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {activeFilterCount > 0 && (
                      <span
                        aria-hidden
                        className="absolute -top-0.5 -right-0.5 size-4 inline-flex items-center justify-center rounded-full text-[10px] font-semibold leading-none"
                        style={{
                          background: 'var(--fd-accent)',
                          color: 'var(--fd-bg)',
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </Container>
    </div>
  );
}
